// eslint-disable-next-line max-classes-per-file
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from '../activity.adapter';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { SemanticModelRefreshActivity } from 'app/model/pipeline/semantic-model-refresh.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import {
    AdapterRegistry,
    ModelConstants,
    convertParametersToModel,
    isLogicAppExpression
} from 'shell-models-lib';
import { PowerBILinkedService } from 'app/model/linkedservice/powerbi.linkedservice';
import { getFabricLsForParameterizedConnections } from '../../utils';
import { ActivityConnectionContext, IPbiSemanticModelRefreshActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { CopilotNameMapperService } from 'app/services/copilot-name-mapper.service';
import { findLinkedServiceFromContext } from 'app/shared/utils/update-pipeline-skill.util';
import { ModelService } from 'app/model/services/model.service';
import { IFrameService } from 'app/services/iframe.service';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SemanticModelRefreshActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: '*',
        externalReferences: '*',
    }
})
export class SemanticModelRefreshActivityAdapter extends ActivityAsyncAdapter<SemanticModelRefreshActivity, Model.ExecutionActivity, IPbiSemanticModelRefreshActivityContext> {
    public isTrident: boolean = __IS_FABRIC_DPE__;

    public model(): IModel {
        return Metamodel.modelFor(SemanticModelRefreshActivity);
    }

    public override async toFabricCopilotContext(activity: SemanticModelRefreshActivity): Promise<IPbiSemanticModelRefreshActivityContext> {
        if (activity) {
            const activityContext: IPbiSemanticModelRefreshActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.powerBILinkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    PowerBILinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.powerBILinkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.powerBILinkedService);
            }
            activityContext.WorkspaceId = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.WORKSPACEID, true); 
            activityContext.SemanticModelId = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.DATASETID, true);
            const nameMapperService = await getServiceInstance<CopilotNameMapperService>(
                this.context.contextData,
                'CopilotNameMapperService'
            );
            const [pbiSemanticModelName, pbiSemanticModelWorkspaceName] = await Promise.all([
                nameMapperService.getDatasetNameById(activityContext.WorkspaceId, activityContext.SemanticModelId),
                nameMapperService.getTridentArtifactNameById('workspace', activityContext.WorkspaceId)
            ]);
            activityContext.SemanticModelName = pbiSemanticModelName;
            activityContext.WorkspaceName = pbiSemanticModelWorkspaceName;
            activityContext.TableNames = (activity.tableIds || []).map((table) => table.table);
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IPbiSemanticModelRefreshActivityContext,
        model?: SemanticModelRefreshActivity
    ): Promise<SemanticModelRefreshActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model) as SemanticModelRefreshActivity;
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                model.powerBILinkedService = (
                    await findLinkedServiceFromContext<PowerBILinkedService>(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.powerBILinkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                                PowerBILinkedService,
                                Model.LinkedServiceResource,
                                ActivityConnectionContext
                            >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    await lsAdapter?.fromFabricCopilotContext(context.Connection, model.powerBILinkedService);
                }
            }
            ValueAdapter.toModel(model, SemanticModelRefreshActivity.WORKSPACEID, context.WorkspaceId);
            ValueAdapter.toModel(model, SemanticModelRefreshActivity.DATASETID, context.SemanticModelId);
            ValueAdapter.toModel(model, SemanticModelRefreshActivity.TABLEIDS, (context.TableNames ?? []).map((table) => ({ table })));
        }
        return model;
    }

    public async postToModel(activity: SemanticModelRefreshActivity): Promise<void> {
        await super.postToModel(activity);
        if (!__IS_FABRIC_DPE__) {
            return;
        }
        const nameMapperService = await getServiceInstance<CopilotNameMapperService>(
            this.context.contextData,
            'CopilotNameMapperService'
        );
        const workspaceId = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.WORKSPACEID, true);
        const datasetId = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.DATASETID, true);
        
        if (workspaceId && !isLogicAppExpression(workspaceId)) {
            nameMapperService.getTridentArtifactNameById('workspace', workspaceId);
        }
        
        if (workspaceId && !isLogicAppExpression(workspaceId) && datasetId && !isLogicAppExpression(datasetId)) {
            nameMapperService.getDatasetNameById(workspaceId, datasetId);
        }
    }

    protected async _toResource(activity: SemanticModelRefreshActivity): Promise<Model.ExecutionActivity> {
        const modelActivity: Model.ExecutionActivity = super._getCommonActivityProperties(
            activity,
            Metamodel.modelOf(activity).name
        ) as Model.ExecutionActivity;

        modelActivity.typeProperties = {
            method: activity.method,
            operationType: 'SemanticModelRefresh'
        };
        modelActivity.typeProperties.groupId = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.WORKSPACEID);
        modelActivity.typeProperties.datasetId = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.DATASETID);
        modelActivity.typeProperties.objects = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.TABLEIDS);

        const waitOnCompletion = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.WAIT_ON_COMPLETION);
        if (waitOnCompletion !== undefined && waitOnCompletion !== null) {
            modelActivity.typeProperties.waitOnCompletion = waitOnCompletion;
        }

        const maxParallelism = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.MAX_PARALLELISM);
        if (maxParallelism !== undefined && maxParallelism !== null) {
            modelActivity.typeProperties.maxParallelism = maxParallelism;
        }

        const retryCount = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.RETRY_COUNT);
        if (retryCount !== undefined && retryCount !== null) {
            modelActivity.typeProperties.retryCount = retryCount;
        }

        const timeout = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.SEMANTIC_MODEL_REFRESH_TIMEOUT);
        if (timeout !== undefined && timeout !== null) {
            modelActivity.typeProperties.timeout = timeout;
        }

        const commitMode = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.COMMIT_MODE);
        if (commitMode !== undefined && commitMode !== null) {
            modelActivity.typeProperties.commitMode = commitMode;
        }

        const refreshType = ValueAdapter.toResource(activity, SemanticModelRefreshActivity.REFRESH_TYPE);
        if (GlobalVariables.enableRefreshTypeSemanticModel && refreshType !== undefined && refreshType !== null) {
            modelActivity.typeProperties.type = refreshType;
        }

        if (activity.powerBILinkedService) {
            modelActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                activity.powerBILinkedService,
                activity.linkedServiceParameters
            );
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            // Overlay this on http activity
            _.merge(modelActivity, parsed);
        }

        return modelActivity;
    }

    protected async _toModel(modelActivity: Model.ExecutionActivity): Promise<SemanticModelRefreshActivity> {
        const metaModel = Metamodel.modelByName(modelActivity.type);
        const activity = metaModel.create<SemanticModelRefreshActivity>(modelActivity.name);

        this._updatedCommonProperties(modelActivity, activity);

        if (modelActivity.typeProperties) {
            this._updatedCommonProperties(modelActivity, activity);

            activity.method = modelActivity.typeProperties.method
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ? (modelActivity.typeProperties.method.toUpperCase() as any)
                : undefined;

            activity.path = modelActivity.typeProperties.path;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ValueAdapter.toModel(activity, SemanticModelRefreshActivity.WORKSPACEID, modelActivity.typeProperties.groupId);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ValueAdapter.toModel(activity, SemanticModelRefreshActivity.DATASETID, modelActivity.typeProperties.datasetId);

            if (modelActivity.typeProperties?.objects) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ValueAdapter.toModel(activity, SemanticModelRefreshActivity.TABLEIDS, modelActivity.typeProperties.objects);
            }

            if (modelActivity.typeProperties.waitOnCompletion !== undefined) {
                ValueAdapter.toModel(activity, SemanticModelRefreshActivity.WAIT_ON_COMPLETION, modelActivity.typeProperties.waitOnCompletion);
            }

            if (modelActivity.typeProperties.maxParallelism !== undefined) {
                ValueAdapter.toModel(activity, SemanticModelRefreshActivity.MAX_PARALLELISM, modelActivity.typeProperties.maxParallelism);
            }

            if (modelActivity.typeProperties.retryCount !== undefined) {
                ValueAdapter.toModel(activity, SemanticModelRefreshActivity.RETRY_COUNT, modelActivity.typeProperties.retryCount);
            }

            if (modelActivity.typeProperties.timeout !== undefined) {
                ValueAdapter.toModel(activity, SemanticModelRefreshActivity.SEMANTIC_MODEL_REFRESH_TIMEOUT, modelActivity.typeProperties.timeout);
            }

            if (modelActivity.typeProperties.commitMode !== undefined) {
                ValueAdapter.toModel(activity, SemanticModelRefreshActivity.COMMIT_MODE, modelActivity.typeProperties.commitMode);
            }

            if (GlobalVariables.enableRefreshTypeSemanticModel && modelActivity.typeProperties.type !== undefined) {
                ValueAdapter.toModel(activity, SemanticModelRefreshActivity.REFRESH_TYPE, modelActivity.typeProperties.type);
            }
        }

        if (modelActivity.linkedServiceName !== undefined) {
            const ls =  getFabricLsForParameterizedConnections(
                modelActivity.linkedServiceName,
                ModelConstants.pbiDatasetsLinkedService, 
                activity);
            activity.powerBILinkedService = ls ? (ls as PowerBILinkedService) :
                ReferenceAdapter.toProxyModel<PowerBILinkedService>(
                    Metamodel.modelFor(PowerBILinkedService),
                    modelActivity.linkedServiceName,
                    this.context
                    );                

            if (modelActivity.linkedServiceName.parameters) {
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.powerBILinkedService,
                    activity.linkedServiceParameters,
                    modelActivity.linkedServiceName.parameters
                );
            }
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, metaModel));

        return activity;
    }
}
